import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDatePicker from '../common/SalovareDatePicker';
import SalovareLoader from '../common/SalovareLoader';
import employeePaymentService from '../../services/employeePaymentService';
import { useToast } from '../../context/ToastContext';
import requestDeduplicator from '../../utils/requestDeduplicator';
import EmployeePaymentModes, {
  EmployeePayCadence,
  getPaymentModeKey,
  normalizePayCadence,
  normalizePaymentMode,
  PAY_DAY_DEFAULT,
  PAY_DAY_MAX,
  PAY_DAY_MIN,
  WEEKDAY_OPTIONS,
} from '../../constants/EmployeePaymentModes';
import SalovareSelectField from '../common/SalovareSelectField';
import SwitchField from '../common/SwitchField';
import ScrollableDialog from '../common/ScrollableDialog';
import { BRAND } from '../../theme/brandTokens';

const paymentModeOptions = [
  EmployeePaymentModes.FixedSalaryOnly,
  EmployeePaymentModes.CommissionOnly,
  EmployeePaymentModes.Mixed,
  EmployeePaymentModes.CommissionWithMinimum,
];

export const parseOptionalNumber = (value) => {
  if (value === '' || value == null) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const payDayOptions = Array.from({ length: PAY_DAY_MAX - PAY_DAY_MIN + 1 }, (_, index) => PAY_DAY_MIN + index);

export const validateCompensationForm = ({
  paymentMode,
  fixedMonthlySalary,
  commissionRatePercent,
  minimumMonthlySalary,
  effectiveFrom,
  payCadence,
  payDayOfMonth,
  payDayOfWeek,
}) => {
  const showFixedSalary = paymentMode === EmployeePaymentModes.FixedSalaryOnly
    || paymentMode === EmployeePaymentModes.Mixed;
  const showCommission = paymentMode !== EmployeePaymentModes.FixedSalaryOnly;
  const showMinimum = paymentMode === EmployeePaymentModes.CommissionWithMinimum;

  const fixed = parseOptionalNumber(fixedMonthlySalary);
  const commission = parseOptionalNumber(commissionRatePercent);
  const minimum = parseOptionalNumber(minimumMonthlySalary);

  if (showCommission && (commission == null || commission < 0 || commission > 100)) {
    return 'commissionRange';
  }

  if (showFixedSalary && (fixed == null || fixed <= 0)) {
    return 'fixedRequired';
  }

  if (showMinimum && (minimum == null || minimum <= 0)) {
    return 'minimumRequired';
  }

  if (!effectiveFrom || Number.isNaN(effectiveFrom.getTime())) {
    return 'effectiveFromRequired';
  }

  if (normalizePayCadence(payCadence) === EmployeePayCadence.Monthly) {
    const day = Number.parseInt(payDayOfMonth, 10);
    if (!Number.isFinite(day) || day < PAY_DAY_MIN || day > PAY_DAY_MAX) {
      return 'payDayOfMonthRange';
    }
  } else if (payDayOfWeek == null || payDayOfWeek === '') {
    return 'payDayOfWeekRequired';
  }

  return null;
};

const TeamMemberCompensationForm = forwardRef(function TeamMemberCompensationForm({
  storeId,
  teamMemberId,
  canEdit = false,
  onSaved,
  showSaveButton = true,
}, ref) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [formErrorKey, setFormErrorKey] = useState(null);
  const [paymentMode, setPaymentMode] = useState(EmployeePaymentModes.CommissionOnly);
  const [fixedMonthlySalary, setFixedMonthlySalary] = useState('');
  const [commissionRatePercent, setCommissionRatePercent] = useState('');
  const [minimumMonthlySalary, setMinimumMonthlySalary] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date());
  const [payCadence, setPayCadence] = useState(EmployeePayCadence.Monthly);
  const [payDayOfMonth, setPayDayOfMonth] = useState(PAY_DAY_DEFAULT);
  const [payDayOfWeek, setPayDayOfWeek] = useState(1);
  const [applyDiscountDeductions, setApplyDiscountDeductions] = useState(false);
  const [discountsHelpOpen, setDiscountsHelpOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!storeId || !teamMemberId) {
      setLoading(false);
      return;
    }

    const requestKey = `team-member-compensation-form-${storeId}-${teamMemberId}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const compensation = await employeePaymentService.getTeamMemberCompensation(storeId, teamMemberId);

        setPaymentMode(normalizePaymentMode(compensation.paymentMode));
        setFixedMonthlySalary(compensation.fixedMonthlySalary != null ? String(compensation.fixedMonthlySalary) : '');
        setCommissionRatePercent(compensation.commissionRatePercent != null ? String(compensation.commissionRatePercent) : '');
        setMinimumMonthlySalary(compensation.minimumMonthlySalary != null ? String(compensation.minimumMonthlySalary) : '');
        setEffectiveFrom(compensation.effectiveFrom ? new Date(compensation.effectiveFrom) : new Date());
        setPayCadence(normalizePayCadence(compensation.payCadence));
        setPayDayOfMonth(compensation.payDayOfMonth ?? PAY_DAY_DEFAULT);
        setPayDayOfWeek(compensation.payDayOfWeek ?? 1);
        setApplyDiscountDeductions(Boolean(compensation.applyDiscountDeductions));
      } catch (error) {
        console.error('Failed to load team member compensation:', error);
        setLoadError(t('employeePayments.compensation.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [storeId, teamMemberId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showFixedSalary = useMemo(
    () => paymentMode === EmployeePaymentModes.FixedSalaryOnly || paymentMode === EmployeePaymentModes.Mixed,
    [paymentMode],
  );
  const showCommission = useMemo(
    () => paymentMode !== EmployeePaymentModes.FixedSalaryOnly,
    [paymentMode],
  );
  const showMinimum = useMemo(
    () => paymentMode === EmployeePaymentModes.CommissionWithMinimum,
    [paymentMode],
  );

  const showWeeklyPay = payCadence === EmployeePayCadence.Weekly;

  const handleSave = useCallback(async () => {
    const validationKey = validateCompensationForm({
      paymentMode,
      fixedMonthlySalary,
      commissionRatePercent,
      minimumMonthlySalary,
      effectiveFrom,
      payCadence,
      payDayOfMonth,
      payDayOfWeek,
    });

    if (validationKey) {
      setFormErrorKey(validationKey);
      return false;
    }

    setFormErrorKey(null);

    try {
      setSaving(true);
      const effectiveFromValue = effectiveFrom.toISOString().slice(0, 10);

      await employeePaymentService.updateTeamMemberCompensation(storeId, teamMemberId, {
        paymentMode,
        fixedMonthlySalary: showFixedSalary ? parseOptionalNumber(fixedMonthlySalary) : null,
        commissionRatePercent: showCommission ? parseOptionalNumber(commissionRatePercent) : null,
        minimumMonthlySalary: showMinimum ? parseOptionalNumber(minimumMonthlySalary) : null,
        effectiveFrom: effectiveFromValue,
        payCadence,
        payDayOfMonth: showWeeklyPay ? PAY_DAY_DEFAULT : Number(payDayOfMonth),
        payDayOfWeek: showWeeklyPay ? Number(payDayOfWeek) : 1,
        applyDiscountDeductions: showCommission ? applyDiscountDeductions : false,
      });

      showSuccess(t('employeePayments.compensation.success.saved'));
      if (onSaved) {
        onSaved();
      }
      return true;
    } catch (error) {
      console.error('Failed to save compensation:', error);
      showError(t('employeePayments.compensation.error.save'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    commissionRatePercent,
    effectiveFrom,
    fixedMonthlySalary,
    minimumMonthlySalary,
    onSaved,
    paymentMode,
    showCommission,
    showFixedSalary,
    showMinimum,
    payCadence,
    payDayOfMonth,
    payDayOfWeek,
    applyDiscountDeductions,
    showWeeklyPay,
    showError,
    showSuccess,
    storeId,
    t,
    teamMemberId,
  ]);

  useImperativeHandle(ref, () => ({
    save: handleSave,
    saving,
    loading,
    loadError,
    canSave: canEdit && !loadError && !loading,
  }), [canEdit, handleSave, loadError, loading, saving]);

  if (loading) {
    return <SalovareLoader size="small" center sx={{ py: 3 }} />;
  }

  return (
    <Stack spacing={2.5}>
      {loadError && <Alert severity="error">{loadError}</Alert>}
      {formErrorKey && (
        <Alert severity="error">{t(`employeePayments.compensation.error.${formErrorKey}`)}</Alert>
      )}

      <FormControl fullWidth disabled={!canEdit}>
        <InputLabel id={`payment-mode-label-${teamMemberId}`}>{t('employeePayments.compensation.paymentMode')}</InputLabel>
        <Select
          labelId={`payment-mode-label-${teamMemberId}`}
          label={t('employeePayments.compensation.paymentMode')}
          value={paymentMode}
          onChange={(event) => setPaymentMode(event.target.value)}
          MenuProps={{ disableScrollLock: true }}
        >
          {paymentModeOptions.map((mode) => {
            const key = getPaymentModeKey(mode);
            return (
              <MenuItem key={mode} value={mode}>
                {t(`employeePayments.compensation.modes.${key}`)}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {showFixedSalary && (
        <TextField
          fullWidth
          type="number"
          label={t('employeePayments.compensation.fixedMonthlySalary')}
          value={fixedMonthlySalary}
          onChange={(event) => setFixedMonthlySalary(event.target.value)}
          disabled={!canEdit}
          inputProps={{ min: 0, step: '0.01' }}
        />
      )}

      {showCommission && (
        <>
          <TextField
            fullWidth
            type="number"
            label={t('employeePayments.compensation.commissionRatePercent')}
            value={commissionRatePercent}
            onChange={(event) => setCommissionRatePercent(event.target.value)}
            disabled={!canEdit}
            inputProps={{ min: 0, max: 100, step: '0.01' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 0.5 }}>
              {t('employeePayments.compensation.deductions.title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <SwitchField
                checked={applyDiscountDeductions}
                onChange={(event) => setApplyDiscountDeductions(event.target.checked)}
                disabled={!canEdit}
                label={t('employeePayments.compensation.deductions.discounts')}
              />
              <IconButton
                type="button"
                size="small"
                aria-label={t('employeePayments.compensation.deductions.discountsHelpAriaLabel')}
                onClick={() => setDiscountsHelpOpen(true)}
                sx={{
                  p: 0.25,
                  color: BRAND.textSubtle,
                  '&:hover': {
                    color: BRAND.accentText,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <ScrollableDialog
              open={discountsHelpOpen}
              onClose={() => setDiscountsHelpOpen(false)}
              maxWidth="sm"
              showCloseButton
              dividers
              title={t('employeePayments.compensation.deductions.discountsHelpTitle')}
              titleSx={{ pb: 1, flexShrink: 0 }}
              actionsSx={{ flexShrink: 0, justifyContent: 'flex-end' }}
              actions={(
                <PrimaryActionButton onClick={() => setDiscountsHelpOpen(false)}>
                  {t('common.close')}
                </PrimaryActionButton>
              )}
            >
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  {t('employeePayments.compensation.deductions.discountsHelpOff')}
                </Typography>
                <Typography variant="body2">
                  {t('employeePayments.compensation.deductions.discountsHelpOn')}
                </Typography>
                <Typography variant="body2">
                  {t('employeePayments.compensation.deductions.discountsHelpExample')}
                </Typography>
              </Stack>
            </ScrollableDialog>
          </Box>
        </>
      )}

      {showMinimum && (
        <TextField
          fullWidth
          type="number"
          label={t('employeePayments.compensation.minimumMonthlySalary')}
          value={minimumMonthlySalary}
          onChange={(event) => setMinimumMonthlySalary(event.target.value)}
          disabled={!canEdit}
          inputProps={{ min: 0, step: '0.01' }}
        />
      )}

      <SalovareDatePicker
        label={t('employeePayments.compensation.effectiveFrom')}
        value={effectiveFrom}
        onChange={(value) => setEffectiveFrom(value)}
        disabled={!canEdit}
        minDate={new Date(new Date().getFullYear() - 5, new Date().getMonth(), new Date().getDate())}
      />
      <Typography variant="body2" color="text.secondary">
        {t('employeePayments.compensation.effectiveFromHint')}
      </Typography>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.primaryText, mt: 1 }}>
        {t('employeePayments.compensation.payScheduleTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('employeePayments.compensation.payScheduleHint')}
      </Typography>

      <SalovareSelectField
        label={t('employeePayments.compensation.payCadence')}
        value={payCadence}
        onChange={(event) => setPayCadence(event.target.value)}
        disabled={!canEdit}
      >
        <MenuItem value={EmployeePayCadence.Monthly}>
          {t('employeePayments.compensation.payCadenceOptions.monthly')}
        </MenuItem>
        <MenuItem value={EmployeePayCadence.Weekly}>
          {t('employeePayments.compensation.payCadenceOptions.weekly')}
        </MenuItem>
      </SalovareSelectField>

      {showWeeklyPay ? (
        <SalovareSelectField
          label={t('employeePayments.compensation.payDayOfWeek')}
          value={payDayOfWeek}
          onChange={(event) => setPayDayOfWeek(event.target.value)}
          disabled={!canEdit}
        >
          {WEEKDAY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(`employeePayments.compensation.weekdays.${option.key}`)}
            </MenuItem>
          ))}
        </SalovareSelectField>
      ) : (
        <SalovareSelectField
          label={t('employeePayments.compensation.payDayOfMonth')}
          value={payDayOfMonth}
          onChange={(event) => setPayDayOfMonth(event.target.value)}
          disabled={!canEdit}
        >
          {payDayOptions.map((day) => (
            <MenuItem key={day} value={day}>
              {t('employeePayments.compensation.payDayOption', { day })}
            </MenuItem>
          ))}
        </SalovareSelectField>
      )}

      {!canEdit && (
        <Alert severity="info">{t('employeePayments.compensation.readOnlyHint')}</Alert>
      )}

      {canEdit && showSaveButton && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryActionButton onClick={handleSave} disabled={saving || Boolean(loadError)}>
            {saving ? t('common.saving') : t('common.save')}
          </PrimaryActionButton>
        </Box>
      )}
    </Stack>
  );
});

export default TeamMemberCompensationForm;
